import { useEffect, useState } from "react";
import { SignalPack } from "@/components/rcc/SignalPack";
import { WorkflowScorecard } from "@/components/rcc/WorkflowScorecard";
import { NavLink, Navigate, useParams } from "react-router-dom";
import { OpportunityRadar } from "@/components/radar/Radar";
import { plural } from "@/components/rcc/Cells";
import AdminShell from "@/components/admin/AdminShell";
import { DailyBriefing } from "@/components/admin/DailyBriefing";
import { ResourceTable } from "@/components/rcc/ResourceTable";
import { LinkedInSequences } from "@/components/rcc/li/LinkedInSequences";
import { TodayCalls } from "@/components/rcc/li/TodayCalls";
import { HowItWorks } from "@/components/admin/HowItWorks";
import { RccAnalytics, RccDashboard, RccPipeline, RccStrategist, ReuseLinks } from "@/components/rcc/Special";
import { RESOURCES, SECTIONS } from "@/lib/rcc";
import { TodayFocus } from "@/components/rcc/TodayFocus";
import { I2R } from "@/components/rcc/I2R";
import { WarmPaths } from "@/components/rcc/Relations";
import { useRccAlerts } from "@/lib/rccAlerts";
import { AlertBanner, AlertBell } from "@/components/rcc/Alerts";
import { cn } from "@/lib/utils";
import { Plus, Zap } from "lucide-react";
import { TEMPLATES } from "@/lib/workflowTemplates";
import { useAuth } from "@/context/AuthContext";
import { RADAR_EXTRAS, clientMods, hasExtra, hasRadar } from "@/lib/clients";

const REUSE = {
  companies: [["/admin/cc/prospects", "Prospects CRM (existing users & leads)"]],
  mpc: [["/admin/cc/prospects", "Pick a candidate in Prospects CRM"], ["/admin/cc/resume", "Resume Intelligence"]],
  campaigns: [["/admin/mk", "Email Marketing templates & sequences"]],
  outreach: [["/admin/mk", "Email Marketing (sending, suppression, templates)"]],
  inbox: [["/admin/scans", "Scan Leads replies"], ["/admin/chat-leads", "Chat Leads"]],
  strategist: [["/admin/cc/advisor", "AI Advisor (business-wide)"]],
  intel: [["/admin/cc/ai", "AI Content Studio"]],
  analytics: [["/admin/cc/funnel", "Growth Funnel"], ["/admin/cc/revenue", "Revenue & Referrals"]],
  admin: [["/admin/cc/profit", "AI cost limits & usage"]],
};
const SPECIAL = { dashboard: RccDashboard, pipeline: RccPipeline, strategist: RccStrategist, analytics: RccAnalytics, linkedin: LinkedInSequences, radar: OpportunityRadar };

const MOVED = { companies: "/admin/rcc/radar", contacts: "/admin/rcc/radar/people" };
const CLIENT_COPY = { workflows: ["My Workflows", "Build and run your own opportunity radars. Each run adds matching companies, scored and explained, to your Opportunity Radar."] };

const NewWorkflowCta = () => (
  <div className="flex flex-col gap-4 rounded-2xl border-2 border-[#8224e3]/60 bg-[#8224e3]/10 p-5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between" data-testid="wf-top-cta">
    <div>
      <p className="font-display text-lg text-zinc-50">Start here: create a new workflow</p>
      <p className="mt-1 text-sm text-zinc-400">Click the button to open the builder and set up who you want to find.</p>
    </div>
    <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("rcc-new-workflows"))} className="relative inline-flex shrink-0 items-center overflow-hidden justify-center gap-2 rounded-xl bg-[#8224e3] px-7 py-4 text-base font-semibold text-white shadow-[0_0_30px_rgba(130,36,227,0.6)] transition-transform hover:scale-105 hover:bg-[#9440f0]" data-testid="wf-top-new-btn">
      <span className="absolute inset-0 animate-ping rounded-xl bg-[#8224e3] opacity-30 [animation-duration:2s]" aria-hidden />
      <Plus className="relative h-5 w-5" /><span className="relative">New workflow</span>
    </button>
    <div className="w-full sm:basis-full" data-testid="wf-templates">
      <p className="mb-2 text-xs text-zinc-400">Or start from a ready-made template (opens pre-filled, you just save):</p>
      <div className="flex flex-wrap gap-2">{TEMPLATES.map((t) => (
        <button key={t.key} type="button" title={t.description} onClick={() => window.dispatchEvent(new CustomEvent("rcc-new-workflows", { detail: t }))} className="inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/5 px-3 py-1.5 text-xs font-medium text-[#F3C649] transition-colors hover:bg-[#D4AF37]/15" data-testid={`wf-tpl-${t.key}`}><Zap className="h-3.5 w-3.5" />{t.label}</button>))}</div>
    </div>
  </div>
);

export default function RevenueCenter({ radar }) {
  const { client } = useAuth();
  const { section: s = client ? "workflows" : "dashboard" } = useParams();
  const section = radar ? "radar" : s;
  const sections = client ? SECTIONS.filter(([k]) => (k === "radar" && hasRadar(client)) || (k === "workflows" && clientMods(client).workflows !== false) || (RADAR_EXTRAS.some(([x]) => x === k) && hasExtra(client, k))) : SECTIONS;
  const [id, label, blurb, resources] = sections.find((s) => s[0] === section) || sections[0];
  const Special = SPECIAL[id];
  const alerts = useRccAlerts();
  const [tick, setTick] = useState(0);
  useEffect(() => { document.querySelector(`[data-testid=rcc-nav-${id}]`)?.scrollIntoView({ inline: "center", block: "nearest" }); }, [id]);
  if (MOVED[s] && !radar) return <Navigate to={MOVED[s]} replace />;
  if (client && !radar && RADAR_EXTRAS.some(([x]) => x === s)) return <Navigate to={`/admin/rcc/radar/${s}`} replace />;
  return (
    <AdminShell area="rcc" title={client ? (id === "radar" ? "Opportunity Radar" : `${client.company || "Your"} workspace`) : "Revenue Command Center"} subtitle={client ? (id === "radar" ? "Your companies, signals and decision makers, scored by your own workflows." : null) : "Win the recruiting assignment. Place the candidate. One system, two revenue paths."}>
      <div className={client ? "grid gap-5" : "grid gap-5 lg:grid-cols-[220px_1fr]"} data-testid="rcc-page">
        {!client && <nav className="flex gap-1 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible" data-testid="rcc-nav">
          {SECTIONS.map(([k, l], i) => (
            <NavLink key={k} to={`/admin/rcc/${k}`} className={() => cn("flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm transition-colors", k === id ? "bg-[#8224e3] font-semibold text-white shadow-[0_0_20px_rgba(130,36,227,0.45)]" : "text-zinc-400 hover:bg-[#8224e3]/10 hover:text-zinc-100")} data-testid={`rcc-nav-${k}`}>
              <span className="font-mono text-[10px] opacity-60">{String(i + 1).padStart(2, "0")}</span>{l}
            </NavLink>
          ))}
        </nav>}
        <div className="min-w-0 space-y-5">
          {!client && <AlertBanner {...alerts} />}
          {!(client && id === "radar") && <div>
            <div className="flex items-start justify-between gap-3"><h2 className="font-display text-2xl text-zinc-50" data-testid="rcc-section-title">{client ? CLIENT_COPY[id]?.[0] || label : label}</h2>{!client && <AlertBell {...alerts} />}</div>
            <p className="mt-1 text-sm text-zinc-400">{client ? CLIENT_COPY[id]?.[1] || blurb : blurb}</p>
            {REUSE[id] && !client && <div className="mt-3"><ReuseLinks links={REUSE[id]} /></div>}
          </div>}
          {id === "workflows" && <NewWorkflowCta />}
          {id !== "radar" && id !== "workflows" && !client && <HowItWorks key={id} id={`rcc-${id}`} />}
          {(id === "radar" || id === "workflows") && <HowItWorks key={`use-${id}`} id={`use-${id}`} />}
          {id === "dashboard" && <TodayFocus />}
          {id === "dashboard" && <WarmPaths />}
          {id === "dashboard" && <TodayCalls />}
          {id === "analytics" && <I2R />}
          {Special && <Special />}
          {id === "workflows" && !client && <SignalPack onInstalled={() => setTick((t) => t + 1)} />}
          {id === "workflows" && <WorkflowScorecard key={`sc-${tick}`} />}
          {id !== "radar" && (resources || []).map((r) => <ResourceTable key={`${r}-${tick}`} name={r} title={resources.length > 1 ? (([n]) => `${n[0].toUpperCase()}${n.slice(1)}`)([plural(RESOURCES[r].noun)]) : null} />)}
          {id === "workflows" && !client && <DailyBriefing />}
        </div>
      </div>
    </AdminShell>
  );
}
